import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Calculator, Play, Save, CheckCircle2, AlertTriangle, MapPin, Navigation, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const tl = (n, dec = 0) => (Number(n) || 0).toLocaleString("tr-TR", { minimumFractionDigits: dec, maximumFractionDigits: dec });

function Fold({ title, tone, tid, children, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={tid} className={`rounded-lg border border-slate-800 bg-slate-950/40 ${className}`}>
      <button data-testid={`${tid}-toggle`} onClick={() => setOpen(!open)} className={`flex w-full items-center gap-1 px-3 py-2 text-left text-xs font-bold ${tone}`}>
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />} {title}
      </button>
      {open && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
}

function ResultCard({ data, customers, onSaved }) {
  const [cid, setCid] = useState(data.matched_id || "");
  const [pick, setPick] = useState("");
  const [saving, setSaving] = useState(false);
  const ic = data.il_ciro;

  const resolve = () => {
    if (cid) return cid;
    const f = customers.find((c) => (c.data?.firma || "").toLocaleUpperCase("tr-TR") === pick.toLocaleUpperCase("tr-TR"));
    return f?.id || "";
  };
  const save = async () => {
    const id = resolve();
    if (!id) { toast.error("MÜŞTERİ SEÇİN"); return; }
    setSaving(true);
    try {
      await api.post("/hesaplama/save", { data: { customer_id: id, il_ciro_text: data.il_ciro_text, rut_text: data.rut_text, teklif_text: data.teklif_text, cikis_ciro_text: data.cikis_ciro_text, hesap: { il_ciro: data.il_ciro, rut: data.rut, teklif: data.teklif, il_ciro_text: data.il_ciro_text, rut_text: data.rut_text, teklif_text: data.teklif_text, cikis_ciro: data.cikis_ciro, cikis_teklif: data.cikis_teklif, cikis_ciro_text: data.cikis_ciro_text, has_cikis: data.has_cikis } } });
      toast.success("MÜŞTERİYE KAYDEDİLDİ");
      onSaved && onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="hesap-result-card" className="rounded-xl border border-slate-800 bg-slate-900 p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-head text-lg font-bold text-white">{data.name || "GENEL"}</span>
          {data.matched_id
            ? <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-bold text-emerald-300"><CheckCircle2 className="mr-1 inline h-3 w-3" />SİSTEMDE EŞLEŞTİ</span>
            : <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold text-amber-300">EŞLEŞMEDİ — SEÇİN</span>}
        </div>
        <div className="flex items-center gap-2">
          {!data.matched_id && (
            <input data-testid="hesap-cust-pick" list="hesap-custs" value={pick} onChange={(e) => setPick(e.target.value)} placeholder="MÜŞTERİ SEÇ..." className="h-9 w-56 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-100" />
          )}
          <Button data-testid="hesap-save" onClick={save} disabled={saving} className="h-9 bg-emerald-600 text-white hover:bg-emerald-700"><Save className="mr-1 h-4 w-4" /> MÜŞTERİYE KAYDET</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 items-start gap-2 lg:grid-cols-4">
        {/* IL CIROLARI */}
        <Fold tid="hesap-fold-varis" tone="text-sky-700" title={<><MapPin className="h-3.5 w-3.5" /> İL CİROLARI (VARIŞ) · {tl(ic.genel)}</>}>
          <table className="w-full text-xs">
            <thead><tr className="text-slate-500"><th className="text-left font-normal">VARIŞ İLİ</th><th className="text-right font-normal">CİRO</th></tr></thead>
            <tbody>
              {ic.group1.map((x) => <tr key={x.il} className="text-slate-200"><td className="py-0.5">{x.il}</td><td className="py-0.5 text-right font-mono">{tl(x.ciro)}</td></tr>)}
              <tr className="border-t border-slate-700 font-bold text-sky-200"><td className="py-0.5">Ara Toplam</td><td className="py-0.5 text-right font-mono">{tl(ic.ara1)}</td></tr>
              <tr><td colSpan={2} className="py-1"><div className="border-t border-dashed border-slate-700" /></td></tr>
              {ic.group2.map((x) => <tr key={x.il} className="text-slate-300"><td className="py-0.5">{x.il}</td><td className="py-0.5 text-right font-mono">{tl(x.ciro)}</td></tr>)}
              <tr className="border-t border-slate-700 font-bold text-sky-200"><td className="py-0.5">Ara Toplam</td><td className="py-0.5 text-right font-mono">{tl(ic.ara2)}</td></tr>
              <tr className="border-t-2 border-slate-600 text-base font-bold text-orange-300"><td className="py-1">GENEL TOPLAM</td><td className="py-1 text-right font-mono">{tl(ic.genel)}</td></tr>
            </tbody>
          </table>
        </Fold>

        {/* CIKIS IL CIROLARI */}
        {data.has_cikis && data.cikis_ciro && (
        <Fold tid="hesap-cikis-card" tone="text-fuchsia-700" title={<><Navigation className="h-3.5 w-3.5" /> ÇIKIŞ İL CİROLARI · {tl(data.cikis_ciro.grand)}</>}>
          <table className="w-full text-xs">
            <thead><tr className="text-slate-500"><th className="text-left font-normal">ÇIKIŞ İLİ</th><th className="text-right font-normal">CİRO</th></tr></thead>
            <tbody>
              {data.cikis_ciro.group1.map((x) => <tr key={x.il} className="text-slate-200"><td className="py-0.5">{x.il}</td><td className="py-0.5 text-right font-mono">{tl(x.ciro)}</td></tr>)}
              {data.cikis_ciro.group1.length > 0 && <tr className="border-t border-slate-700 font-bold text-fuchsia-200"><td className="py-0.5">Ara Toplam</td><td className="py-0.5 text-right font-mono">{tl(data.cikis_ciro.ara1)}</td></tr>}
              <tr><td colSpan={2} className="py-1"><div className="border-t border-dashed border-slate-700" /></td></tr>
              {data.cikis_ciro.group2.map((x) => <tr key={x.il} className="text-slate-300"><td className="py-0.5">{x.il}</td><td className="py-0.5 text-right font-mono">{tl(x.ciro)}</td></tr>)}
              {data.cikis_ciro.group2.length > 0 && <tr className="border-t border-slate-700 font-bold text-fuchsia-200"><td className="py-0.5">Ara Toplam</td><td className="py-0.5 text-right font-mono">{tl(data.cikis_ciro.ara2)}</td></tr>}
              {data.cikis_ciro.missing_count > 0 && <tr data-testid="hesap-cikis-missing" className="font-bold text-rose-400"><td className="py-0.5">⚠ İL YAZILMAMIŞ/TANINMADI ({data.cikis_ciro.missing_count})</td><td className="py-0.5 text-right font-mono">{tl(data.cikis_ciro.missing)}</td></tr>}
              <tr className="border-t-2 border-slate-600 text-base font-bold text-orange-300"><td className="py-1">GENEL TOPLAM</td><td className="py-1 text-right font-mono">{tl(data.cikis_ciro.grand)}</td></tr>
            </tbody>
          </table>
          {data.cikis_ciro.missing_count > 0 && <div className="mt-1 text-[10px] font-semibold text-rose-400">KIRMIZI SATIRLARDAKİ {data.cikis_ciro.missing_count} KAYITTA ÇIKIŞ İLİ YOK/TANINMADI. DOĞRU İL YAZINCA VARIŞ İLE AYNI TUTAR OLUR.</div>}
        </Fold>
        )}

        {/* RUT */}
        <Fold tid="hesap-fold-rut" tone="text-emerald-700" title={<><Navigation className="h-3.5 w-3.5" /> RUT ({data.rut.length})</>}>
          <div className="flex flex-col gap-1">
            {data.rut.map((r) => <div key={r.il} className="flex items-center justify-between rounded bg-slate-800 px-2 py-0.5 text-xs text-slate-200"><span className="font-mono font-bold text-emerald-300">{r.code}</span><span className="text-slate-500">{r.il}</span></div>)}
          </div>
        </Fold>

        {/* TEKLIF */}
        <Fold tid="hesap-fold-teklif" tone="text-amber-700" title="BAREM FİYAT TEKLİFİ">
          <div className="max-h-80 space-y-2 overflow-auto">
            {data.teklif.map((t) => (
              <div key={t.il}>
                <div className="text-xs font-bold text-slate-200">{t.il}:</div>
                {t.buckets.map((b, i) => (
                  <div key={i} className={`flex items-center justify-between rounded px-2 py-0.5 text-xs ${b.outlier ? "bg-amber-400/20 text-amber-200" : "text-slate-300"}`}>
                    <span>{b.outlier && <AlertTriangle className="mr-1 inline h-3 w-3 text-amber-400" />}{b.kind} {Math.round(b.avg_desi)} DESİ</span>
                    <span className="font-mono">{tl(b.avg_fiyat, 2)} TL</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Fold>
      </div>
    </div>
  );
}

export default function Hesaplama() {
  const [text, setText] = useState("");
  const [hasName, setHasName] = useState(true);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);

  useEffect(() => { api.get("/customers?page_size=1000", { silent: true }).then((r) => setCustomers(r.data.items || [])).catch(() => {}); }, []);
  const custNames = useMemo(() => [...new Set(customers.map((c) => c.data?.firma).filter(Boolean))], [customers]);

  const run = async () => {
    if (!text.trim()) { toast.error("ÖNCE VERİ YAPIŞTIRIN"); return; }
    setLoading(true);
    try {
      const r = await api.post("/hesaplama/analyze", { data: { text, has_name: hasName } });
      setResult(r.data);
      if (r.data.row_count === 0) toast.error("SATIR OKUNAMADI — FORMATI KONTROL EDİN");
      else toast.success(`${r.data.row_count} SATIR OKUNDU`);
    } finally { setLoading(false); }
  };

  const cards = result ? (result.has_name ? result.customers : (result.aggregate ? [{ ...result.aggregate, name: "", matched_id: null }] : [])) : [];

  const saveAll = async () => {
    const matched = (result?.customers || []).filter((c) => c.matched_id);
    if (!matched.length) { toast.error("SİSTEMDE EŞLEŞEN MÜŞTERİ YOK"); return; }
    let ok = 0;
    for (const c of matched) {
      try {
        await api.post("/hesaplama/save", { data: { customer_id: c.matched_id, il_ciro_text: c.il_ciro_text, rut_text: c.rut_text, teklif_text: c.teklif_text, cikis_ciro_text: c.cikis_ciro_text, hesap: { il_ciro: c.il_ciro, rut: c.rut, teklif: c.teklif, il_ciro_text: c.il_ciro_text, rut_text: c.rut_text, teklif_text: c.teklif_text, cikis_ciro: c.cikis_ciro, cikis_teklif: c.cikis_teklif, cikis_ciro_text: c.cikis_ciro_text, has_cikis: c.has_cikis } } });
        ok++;
      } catch (e) { /* skip */ }
    }
    toast.success(`${ok} MÜŞTERİYE KAYDEDİLDİ`);
  };

  return (
    <div className="mw-fade-up space-y-4" data-testid="hesaplama-page">
      <datalist id="hesap-custs">{custNames.map((s) => <option key={s} value={s} />)}</datalist>
      <div>
        <h1 className="font-head text-3xl font-bold tracking-tight text-white flex items-center gap-2"><Calculator className="h-7 w-7 text-orange-400" /> HESAPLAMA</h1>
        <p className="text-sm text-slate-400">HAM LİSTEYİ YAPIŞTIR → İL CİROLARI, RUT VE BAREM FİYAT TEKLİFİ OTOMATİK HESAPLANIR</p>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input data-testid="hesap-hasname" type="checkbox" checked={hasName} onChange={(e) => setHasName(e.target.checked)} className="h-4 w-4" />
            Müşteri adı sütunu VAR (YEKÜN 5. sütun) — kapalıysa YEKÜN 4. sütun
          </label>
          <span className="text-xs text-slate-500">Sütun sırası: {hasName ? "MÜŞTERİ · İL · DESİ · FİYAT · YEKÜN" : "İL · DESİ · FİYAT · YEKÜN"} · ÇIKIŞ İLİ (6. sütun, opsiyonel) (tab veya 2+ boşlukla ayrılmış)</span>
        </div>
        <textarea data-testid="hesap-text" value={text} onChange={(e) => setText(e.target.value)} rows={4}
          placeholder={"Düz listenizi buraya yapıştırın...\nörn:\nSUN TEKSTİL    İZMİR    45    120,00    5.400\nSUN TEKSTİL    BURSA    300    1.500,00    1.500"}
          className="w-full rounded-md border border-slate-700 bg-slate-800 p-3 font-mono text-sm text-slate-100" />
        <Button data-testid="hesap-run" onClick={run} disabled={loading} className="h-10 bg-orange-600 text-white hover:bg-orange-700"><Play className="mr-1 h-4 w-4" /> {loading ? "HESAPLANIYOR..." : "HESAPLAMA BAŞLAT"}</Button>
      </div>

      {result?.has_name && cards.some((c) => c.matched_id) && (
        <div className="flex items-center justify-between rounded-lg border border-emerald-900/40 bg-emerald-950/20 px-3 py-2">
          <span className="text-xs text-emerald-200">{cards.filter((c) => c.matched_id).length} müşteri sistemde eşleşti — hepsini tek tuşla kaydedebilirsiniz.</span>
          <Button data-testid="hesap-saveall" onClick={saveAll} className="h-9 bg-emerald-600 text-white hover:bg-emerald-700"><Save className="mr-1 h-4 w-4" /> TÜMÜNÜ KAYDET (EŞLEŞENLER)</Button>
        </div>
      )}
      {cards.map((c, i) => <ResultCard key={i} data={c} customers={customers} onSaved={() => {}} />)}
    </div>
  );
}
